import {
  Alert,
  App as AntdApp,
  Button,
  Empty,
  Flex,
  Form,
  Input,
  Modal,
  Segmented,
  Space,
  Tag,
  Typography
} from 'antd'
import type { TableColumnsType } from 'antd'
import { Check, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceAgent } from 'src/api/facade/types'
import {
  ContentSpinner,
  DateTimeCell,
  MonoValue,
  ProTableContainer,
  QueryErrorAlert,
  TableActionColumnTitle
} from 'src/components'
import { DEFAULT_PER_PAGE } from 'src/hooks'
import type {
  SquadApproval,
  SquadApprovalStatus
} from 'src/services/agentGovernance'

import { useApprovals, useResolveApproval } from './useAgentGovernance'

const { Text } = Typography

type Props = {
  workspaceId: number
  agent: WorkspaceAgent
}

type StatusFilter = 'all' | SquadApprovalStatus

const STATUS_COLOR: Record<SquadApprovalStatus, string> = {
  pending: 'processing',
  accepted: 'success',
  edited: 'gold',
  rejected: 'error',
  answered: 'default'
}

const previewValue = (value: unknown): string => {
  if (value == null) return '—'
  if (typeof value === 'string')
    return value.length > 48 ? `${value.slice(0, 48)}…` : value
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value)
  return Array.isArray(value) ? `[${value.length}]` : '{…}'
}

/** One-line summary of the paused tool call's arguments. */
const payloadPreview = (payload: Record<string, unknown> | null): string => {
  const entries = Object.entries(payload ?? {})

  return entries
    .slice(0, 3)
    .map(([key, value]) => `${key}: ${previewValue(value)}`)
    .join(' · ')
}

const decisionReason = (approval: SquadApproval): string | undefined => {
  const reason = approval.decision?.reason

  return typeof reason === 'string' && reason.trim() ? reason : undefined
}

/**
 * Human-in-the-loop approvals — filtered to this agent by default (server-side
 * `?agent=`), with a toggle to show every agent in the workspace, plus a status
 * filter. Pending rows can be approved/denied via `/api/chat/resume/`.
 */
export const AgentApprovalsTab = ({ workspaceId, agent }: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const readOnly = agent.canEdit === false

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [denyTarget, setDenyTarget] = useState<SquadApproval | null>(null)
  const [denyForm] = Form.useForm<{ reason?: string }>()

  const approvalsQuery = useApprovals(workspaceId, agent.id)
  const resolve = useResolveApproval(workspaceId, agent.id)

  const approve = (approval: SquadApproval) => {
    resolve.mutate(
      { approvalId: approval.id, action: 'accept' },
      {
        onSuccess: () =>
          message.success(t('workspace.agents.approvals.messages.approved')),
        onError: () =>
          message.error(t('workspace.agents.approvals.messages.failed'))
      }
    )
  }

  const deny = (approval: SquadApproval, reason?: string) => {
    resolve.mutate(
      { approvalId: approval.id, action: 'reject', reason },
      {
        onSuccess: () => {
          message.success(t('workspace.agents.approvals.messages.denied'))
          setDenyTarget(null)
          denyForm.resetFields()
        },
        onError: () =>
          message.error(t('workspace.agents.approvals.messages.failed'))
      }
    )
  }

  if (approvalsQuery.isLoading) {
    return <ContentSpinner minHeight={220} />
  }

  if (approvalsQuery.isError) {
    return <QueryErrorAlert onRetry={() => void approvalsQuery.refetch()} />
  }

  const approvals = approvalsQuery.data ?? []
  const pendingCount = approvals.filter((a) => a.status === 'pending').length
  const rows =
    statusFilter === 'all'
      ? approvals
      : approvals.filter((a) => a.status === statusFilter)

  const statusOptions = [
    { label: t('workspace.agents.approvals.filterAll'), value: 'all' },
    { label: t('workspace.agents.approvals.status.pending'), value: 'pending' },
    {
      label: t('workspace.agents.approvals.status.accepted'),
      value: 'accepted'
    },
    {
      label: t('workspace.agents.approvals.status.rejected'),
      value: 'rejected'
    }
  ]

  const columns: TableColumnsType<SquadApproval> = [
    {
      title: t('workspace.agents.approvals.columns.action'),
      key: 'action',
      fixed: 'left',
      render: (_, approval) => {
        const reason = decisionReason(approval)

        return (
          <Flex vertical gap={2} style={{ minWidth: 0 }}>
            <MonoValue copyable={false} ellipsis>
              {approval.tool_name}
            </MonoValue>
            <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
              {reason ?? payloadPreview(approval.payload)}
            </Text>
          </Flex>
        )
      }
    },
    {
      title: t('workspace.agents.approvals.columns.status'),
      key: 'status',
      width: 130,
      render: (_, approval) => (
        <Tag
          color={STATUS_COLOR[approval.status]}
          style={{ marginInlineEnd: 0, fontSize: 11 }}
        >
          {t(`workspace.agents.approvals.status.${approval.status}`)}
        </Tag>
      )
    },
    {
      title: t('workspace.agents.approvals.columns.when'),
      key: 'when',
      width: 150,
      render: (_, approval) => (
        <DateTimeCell
          value={approval.resolved_at ?? approval.created_at}
          variant="relative"
          style={{ fontSize: 12 }}
        />
      )
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 180,
      align: 'right',
      render: (_, approval) =>
        approval.status === 'pending' ? (
          <Space size={4}>
            <Button
              size="small"
              type="primary"
              icon={<Check size={14} />}
              disabled={readOnly || resolve.isPending}
              loading={
                resolve.isPending &&
                resolve.variables?.approvalId === approval.id &&
                resolve.variables?.action === 'accept'
              }
              onClick={() => approve(approval)}
            >
              {t('workspace.agents.approvals.approve')}
            </Button>
            <Button
              size="small"
              danger
              icon={<X size={14} />}
              disabled={readOnly || resolve.isPending}
              onClick={() => setDenyTarget(approval)}
            >
              {t('workspace.agents.approvals.deny')}
            </Button>
          </Space>
        ) : null
    }
  ]

  return (
    <Flex vertical gap={14}>
      <Alert
        type="info"
        showIcon
        message={t('workspace.agents.approvals.introTitle')}
        description={t('workspace.agents.approvals.introBody')}
      />

      <ProTableContainer<SquadApproval>
        showSearch={false}
        rowKey="id"
        columns={columns}
        dataSource={rows}
        pagination={{
          defaultPageSize: DEFAULT_PER_PAGE,
          size: 'small',
          hideOnSinglePage: true
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {t('workspace.agents.approvals.pendingEmpty')}
                </Text>
              }
            />
          )
        }}
        leftChildren={
          <Segmented
            size="small"
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as StatusFilter)}
            options={statusOptions}
          />
        }
        rightChildren={
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('workspace.agents.approvals.pendingCount', {
              count: pendingCount
            })}
          </Text>
        }
      />

      <Modal
        title={t('workspace.agents.approvals.denyTitle')}
        open={!!denyTarget}
        confirmLoading={resolve.isPending}
        onCancel={() => {
          setDenyTarget(null)
          denyForm.resetFields()
        }}
        okText={t('workspace.agents.approvals.deny')}
        okType="danger"
        cancelText={t('workspace.agents.form.cancel')}
        onOk={() => denyForm.submit()}
        destroyOnHidden
      >
        <Text
          type="secondary"
          style={{ display: 'block', marginTop: 8, marginBottom: 8 }}
        >
          {denyTarget?.tool_name}
        </Text>
        <Form
          form={denyForm}
          layout="vertical"
          onFinish={(values) => {
            if (denyTarget) {
              deny(denyTarget, values.reason?.trim() || undefined)
            }
          }}
        >
          <Form.Item
            name="reason"
            label={t('workspace.agents.approvals.reasonLabel')}
          >
            <Input.TextArea
              rows={3}
              maxLength={280}
              showCount
              placeholder={t('workspace.agents.approvals.reasonPlaceholder')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  )
}
